import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { approvalsApi } from "api/approvals";

function labelFor(field) {
  const labels = {
    "contact.fullName": "Name",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "conversation.next_step": "Suggested next step",
    follow_up_draft: "Follow-up draft",
  };
  return labels[field] || field;
}

function displayValue(value, field) {
  if (field === "follow_up_draft") {
    const draft = value || {};
    return draft.content || "Follow-up message";
  }
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    const step = value;
    return [step.action, step.dueAt ? `due ${new Date(step.dueAt).toLocaleDateString("en-IN")}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  return String(value);
}

/**
 * Approvals queue (Part 72 §72.5, Sprint 3/5): AI field suggestions +
 * follow-up drafts awaiting human decision. Approve writes the canonical
 * change (contact/task/message) — nothing happens without approval.
 */
export default function Approvals() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [selected, setSelected] = useState([]);
  const [isBatchApproving, setIsBatchApproving] = useState(false);
  const [batchNotice, setBatchNotice] = useState("");

  const load = async (status) => {
    try {
      const items = await approvalsApi.list({ status });
      setItems(items);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load the approval queue.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(statusFilter);
  }, [statusFilter]);

  // Sprint 14 (Part 94 §94.1): batch approve — selection is pending-only.
  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelected([]);

  const handleBatchApprove = async () => {
    if (selected.length === 0) return;
    setIsBatchApproving(true);
    setBatchNotice("");
    try {
      const result = await approvalsApi.batchApprove(selected);
      const failedCount = result.failed?.length ?? 0;
      setBatchNotice(
        failedCount === 0
          ? `Approved ${result.approved?.length ?? selected.length} suggestions.`
          : `Approved ${result.approved?.length ?? 0}, ${failedCount} could not be approved (already resolved).`,
      );
      clearSelection();
      await load(statusFilter);
    } catch (error) {
      setLoadError(error?.message || "Batch approval failed.");
    } finally {
      setIsBatchApproving(false);
    }
  };

  const toggleSelectAll = () => {
    const pending = items.filter((i) => i.status === "pending").map((i) => i.id);
    const allSelected = pending.length > 0 && pending.every((id) => selected.includes(id));
    setSelected(allSelected ? [] : pending);
  };

  const handle = async (id, action) => {
    setBusyId(id);
    setBusyAction(action);
    try {
      if (action === "approve") await approvalsApi.approve(id);
      else if (action === "reject") await approvalsApi.reject(id);
      else await approvalsApi.undo(id);
      await load(statusFilter);
    } catch (error) {
      setLoadError(error?.message || `Could not ${action}.`);
    } finally {
      setBusyId(null);
      setBusyAction("");
    }
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Approvals
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              AI suggestions and follow-up drafts — approve to apply, reject to discard
            </p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border font-caption"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", height: 38 }}
            aria-label="Filter approvals by status"
          >
            <option value="pending">Pending</option>
            <option value="applied">Applied</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
          </div>
        )}

        {batchNotice && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "#BBF7D0", background: "#F0FDF4" }}>
            <p className="font-caption text-sm font-medium" style={{ color: "#15803D" }}>{batchNotice}</p>
          </div>
        )}

        {/* Sprint 14: batch approve bar */}
        {statusFilter === "pending" && selected.length > 0 && (
          <div className="mb-4 rounded-xl border px-4 py-3 flex items-center justify-between gap-3" style={{ borderColor: "var(--color-primary)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>
              <strong>{selected.length}</strong> selected
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection} disabled={isBatchApproving}>
                Clear
              </Button>
              <Button size="sm" loading={isBatchApproving} onClick={handleBatchApprove}>
                <Icon name="CheckCheck" size={14} style={{ marginRight: 6 }} />
                Approve selected
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading approvals...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="CheckCircle" size={20} color="var(--color-text-secondary)" className="mx-auto mb-2" />
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
              {statusFilter === "pending" ? "No pending approvals. AI suggestions and follow-up drafts will appear here." : "Nothing here."}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            {/* Sprint 14: select-all row (pending view) */}
            {statusFilter === "pending" && (
              <div className="flex items-center gap-3 px-4 py-2 border-b" style={{ borderColor: "var(--color-border)" }}>
                <label className="flex items-center gap-2 cursor-pointer font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  <input
                    type="checkbox"
                    checked={items.filter((i) => i.status === "pending").length > 0 && items.filter((i) => i.status === "pending").every((i) => selected.includes(i.id))}
                    onChange={toggleSelectAll}
                  />
                  Select all pending
                </label>
              </div>
            )}
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1 flex items-start gap-3">
                  {item.status === "pending" && (
                    <input
                      type="checkbox"
                      className="mt-1 shrink-0"
                      checked={selected.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      aria-label={`Select ${item.field}`}
                    />
                  )}
                  <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                      {labelFor(item.field)}
                    </span>
                    <span
                      className="font-caption text-[10px] px-1.5 py-0.5 rounded-full uppercase"
                      style={{
                        background: item.status === "pending" ? "#DBEAFE" : item.status === "applied" ? "#DCFCE7" : "#FEE2E2",
                        color: item.status === "pending" ? "#1D4ED8" : item.status === "applied" ? "#15803D" : "#B91C1C",
                      }}
                    >
                      {item.status}
                    </span>
                  </div>
                  <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
                    {displayValue(item.suggestedValue, item.field)}
                  </p>
                  <p className="font-caption text-[11px] mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                    Confidence {Math.round((item.confidence || 0) * 100)}% · {item.sourceRef || "—"}
                  </p>
                </div>
                </div>
                {item.status === "pending" ? (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button variant="default" size="sm" iconName="Check" iconPosition="left" iconSize={14} loading={busyId === item.id && busyAction === "approve"} onClick={() => handle(item.id, "approve")}>
                      Approve
                    </Button>
                    <Button variant="outline" size="sm" iconName="X" iconPosition="left" iconSize={14} loading={busyId === item.id && busyAction === "reject"} onClick={() => handle(item.id, "reject")}>
                      Reject
                    </Button>
                  </div>
                ) : item.status === "applied" && item.entityType !== "follow_up" ? (
                  // Sprint 15 QA: sent follow-ups can't be undone (backend 409) — no Undo affordance.
                  <Button variant="outline" size="sm" iconName="RotateCcw" iconPosition="left" iconSize={14} loading={busyId === item.id && busyAction === "undo"} onClick={() => handle(item.id, "undo")}>
                    Undo
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t" style={{ borderColor: "var(--color-border)" }}>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            &copy; {new Date().getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            Nothing is sent or written without your approval
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
